import type { store } from 'Store'
import { PTA_REQUEST_HEADER_KEY, SELECTED_PTA_STORAGE_KEY } from 'Util/constants'
import { type AsyncThunk, createAsyncThunk } from '@reduxjs/toolkit'
import { type BaseQueryApi, createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type Method,
  type ResponseType
} from 'axios'
import Cookies from 'js-cookie'

export interface DynamicUrl {
  id?: number | string
}

const defaultAxiosOptions: AxiosRequestConfig = {
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 25000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'Accept-Language': 'fi-FI,fi'
  }
}

const getAxiosInstance = (
  responseType?: ResponseType,
  timeout?: number,
  contentType?: string,
  accept?: string
): AxiosInstance => {
  const axiosOptions = structuredClone(defaultAxiosOptions)
  if (Cookies.get('csrftoken')) {
    if (axiosOptions.headers) {
      axiosOptions.headers['X-CSRFToken'] = Cookies.get('csrftoken')
    }
  }
  if (responseType) {
    axiosOptions.responseType = responseType
  } else {
    axiosOptions.responseType = 'json'
  }

  if (timeout) {
    axiosOptions.timeout = timeout
  }

  if (contentType) {
    axiosOptions.headers['Content-Type'] = contentType
  }

  if (accept) {
    axiosOptions.headers['Accept'] = accept
  }

  if (localStorage.getItem(SELECTED_PTA_STORAGE_KEY)) {
    if (axiosOptions.headers) {
      axiosOptions.headers[PTA_REQUEST_HEADER_KEY] = localStorage.getItem(SELECTED_PTA_STORAGE_KEY)
    }
  }
  return axios.create(axiosOptions)
}

interface BaseApiOptions {
  reducerPath?: string
  tagTypes?: string[]
  baseUrl?: string
  timeout?: number
  keepUnusedDataFor?: number
  prepareHeaders?: (
    headers: Headers,
    api: Pick<BaseQueryApi, 'getState' | 'extra' | 'endpoint' | 'type' | 'forced'>
  ) => Headers
}

const apiRegistry = new Set<ReturnType<typeof createApi>>()

export const createBaseApi = (options: BaseApiOptions = {}) => {
  const {
    reducerPath,
    baseUrl = import.meta.env.VITE_API_BASE_URL,
    timeout = 25000,
    tagTypes,
    keepUnusedDataFor = 30
  } = options

  const api = createApi({
    reducerPath,
    tagTypes,
    keepUnusedDataFor,
    baseQuery: fetchBaseQuery({
      baseUrl,
      timeout,
      credentials: 'include',
      prepareHeaders: (headers, api) => {
        // Set default headers
        headers.set('Content-Type', 'application/json')
        headers.set('Accept', 'application/json')
        headers.set('Accept-Language', 'fi-FI,fi')

        // Add CSRF token if available
        const csrfToken = Cookies.get('csrftoken')
        if (csrfToken) {
          headers.set('X-CSRFToken', csrfToken)
        }

        // If custom prepareHeaders is provided, apply it after default headers
        if (options.prepareHeaders) {
          return options.prepareHeaders(headers, api)
        }

        const selectedPta = localStorage.getItem(SELECTED_PTA_STORAGE_KEY)
        if (selectedPta) {
          headers.set(PTA_REQUEST_HEADER_KEY, selectedPta)
        }
        return headers
      }
    }),
    endpoints: () => ({})
  })

  apiRegistry.add(api)
  return api
}

type AppDispatch = typeof store.dispatch

export const resetAllApiState = (dispatch: AppDispatch) => {
  for (const api of Array.from(apiRegistry.values())) {
    dispatch(api.util.resetApiState())
  }
}

// eslint-disable-next-line
type ThunkAPIConfig = {}
export interface ValidationErrors {
  error: unknown | AxiosError
  status?: number
}

export interface ProductTariffViolation {
  field: string
  message: string
}

export interface ProductTariffError {
  status: number
  message: string
  violations: ProductTariffViolation[] | null
}

export interface ServiceProviderManagementViolation {
  field: string
  message: string
}

export interface ServiceProviderManagementError {
  status: number
  message: string
  violations: ServiceProviderManagementViolation[] | null
}

export interface ControlDataServiceViolation {
  field: string
  message: string
}

export interface ControlDataServiceError {
  message: string
  violations: ControlDataServiceViolation[] | null
}

/**
 *
 * @param type The action type to use for created actions.
 * @param url  Path where to send the request
 * @param method HTTP Method to use, default GET
 * @returns
 */
export const createAxiosAsyncThunk = <Returned, ThunkArg = void>(
  type: string,
  url: string,
  method: Method = 'GET'
): AsyncThunk<Returned, ThunkArg, ThunkAPIConfig> => {
  return createAsyncThunk<Returned, ThunkArg, ThunkAPIConfig>(type, async (data, thunkAPI) => {
    try {
      const response = await getAxiosInstance().request<Returned>({
        url,
        method,
        data
      })
      return response.data
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const error: AxiosError<ValidationErrors> = err // cast the error for access
        if (!error.response) {
          throw err
        }
        // We got validation errors, let's return those so we can reference in our component and set form errors
        return thunkAPI.rejectWithValue({
          ...error.response.data,
          status: error.response.status
        })
      }
      throw err
    }
  })
}

/**
 *
 * @param type The action type to use for created actions.
 * @param url  Path where to send the request
 * @param method HTTP Method to use, default GET
 * @returns
 */
export const createAxiosAsyncThunkWithParams = <Returned, ThunkArg = void>(
  type: string,
  url: string,
  method: Method = 'GET'
): AsyncThunk<Returned, ThunkArg & DynamicUrl, ThunkAPIConfig> => {
  return createAsyncThunk<Returned, ThunkArg & DynamicUrl, ThunkAPIConfig>(
    type,
    async (params, thunkAPI) => {
      try {
        const { id } = params
        let dynamicUrl = url
        if (id) {
          dynamicUrl = `${url}/${id}/`
          delete params.id
        }
        const response = await getAxiosInstance().request<Returned>({
          url: dynamicUrl,
          method,
          params
        })
        return response.data
      } catch (err) {
        if (axios.isAxiosError(err)) {
          const error: AxiosError<ValidationErrors> = err // cast the error for access
          if (!error.response) {
            throw err
          }
          // We got validation errors, let's return those, so we can reference in our component and set form errors
          return thunkAPI.rejectWithValue({
            ...error.response.data,
            status: error.response.status
          })
        }
        throw err
      }
    }
  )
}

export function createAsyncThunkWithErrorHandling<Returned, ThunkArg>(
  typePrefix: string,
  payloadCreator: (arg: ThunkArg, thunkAPI: ThunkAPIConfig) => Promise<Returned>
) {
  return createAsyncThunk<Returned, ThunkArg>(typePrefix, async (arg, { rejectWithValue }) => {
    try {
      return await payloadCreator(arg, { rejectWithValue })
    } catch (err) {
      if (axios.isAxiosError(err)) {
        if (!err.response) {
          throw err
        }
        return rejectWithValue({
          ...err.response.data,
          status: err.response.status
        })
      }
      throw err
    }
  })
}

export { getAxiosInstance, defaultAxiosOptions }
export type { AxiosError }
